import { Link } from 'react-router-dom';
import { Ship, Package, Warehouse, FileCheck, Globe, Truck, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

const services = [
  {
    icon: Ship,
    title: 'Ocean Freight',
    description: 'Full container loads (FCL) and less than container loads (LCL) shipping across all major global trade routes.',
    image: 'https://images.pexels.com/photos/1117210/pexels-photo-1117210.jpeg?auto=compress&cs=tinysrgb&w=600',
  },
  {
    icon: Package,
    title: 'Container Shipping',
    description: 'Standard, refrigerated, and specialized containers for every type of cargo with door-to-door service.',
    image: 'https://customer-assets.emergentagent.com/job_maritime-connect-2/artifacts/101hhl67_Copilot_20251106_011256.png',
  },
  {
    icon: Warehouse,
    title: 'Warehousing',
    description: 'Secure storage facilities at major ports with inventory management and distribution services.',
    image: 'https://images.pexels.com/photos/209251/pexels-photo-209251.jpeg?auto=compress&cs=tinysrgb&w=600',
  },
  {
    icon: FileCheck,
    title: 'Customs Clearance',
    description: 'Expert handling of customs documentation, duties, and regulatory compliance for seamless imports/exports.',
    image: 'https://images.pexels.com/photos/6169668/pexels-photo-6169668.jpeg?auto=compress&cs=tinysrgb&w=600',
  },
  {
    icon: Globe,
    title: 'Project Cargo',
    description: 'Specialized solutions for oversized, heavy-lift, and complex project shipments worldwide.',
    image: 'https://customer-assets.emergentagent.com/job_maritime-connect-2/artifacts/r5hqmknb_step-img.png',
  },
  {
    icon: Truck,
    title: 'Last Mile Delivery',
    description: 'Complete logistics chain from port to final destination with real-time tracking.',
    image: 'https://customer-assets.emergentagent.com/job_maritime-connect-2/artifacts/pwej0hj9_delivery.png',
  },
];

export const ServicesGrid = () => {
  return (
    <section className="py-20 lg:py-32 bg-slate-50" data-testid="services-grid">
      <div className="rsl-container">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <span className="text-safety-orange font-mono text-xs tracking-widest uppercase">
            What We Offer
          </span>
          <h2 className="text-4xl lg:text-5xl font-black text-navy uppercase tracking-tight mt-2 mb-4">
            Comprehensive Shipping Solutions
          </h2>
          <p className="text-slate-600 text-lg">
            From port to port or door to door, we provide end-to-end logistics services tailored to your business needs.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div
              key={service.title}
              className="service-card group"
              data-testid={`service-card-${index}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden mb-6">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-navy/20 group-hover:bg-navy/40 transition-colors" />
                <div className="absolute top-4 left-4 bg-safety-orange p-3">
                  <service.icon className="w-6 h-6 text-white" />
                </div>
              </div>

              {/* Content */}
              <div className="p-6 pt-0">
                <h3 className="text-xl font-bold text-navy mb-3 group-hover:text-safety-orange transition-colors">
                  {service.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  {service.description}
                </p>
                <Link
                  to="/services"
                  className="inline-flex items-center text-safety-orange font-bold text-sm uppercase tracking-wider group-hover:gap-3 gap-2 transition-all"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <Link to="/services">
            <Button
              className="bg-navy hover:bg-navy/90 text-white font-bold uppercase tracking-widest text-sm h-14 px-10"
              data-testid="view-all-services-btn"
            >
              View All Services
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;
